import { Company } from 'src/interfaces/company';
import { isBlankSageCustomerCode, readSageCustomerCode } from './sage-customer-code';

export interface CompanyPage {
  results: Company[];
  total: number;
}

export interface MissingSageCustomerRow {
  companyId: number;
  name: string;
  company: Company;
}

export interface MissingSageCustomerScan {
  rows: MissingSageCustomerRow[];
  companiesDone: number;
  companyTotal: number;
  incomplete: boolean;
}

export interface MissingSageCustomerScanDeps {
  listCompanies: (page: number, limit: number) => Promise<CompanyPage | null>;
  onProgress?: (state: { companiesDone: number; companyTotal: number }) => void;
  isCancelled?: () => boolean;
}

const PAGE_LIMIT = 100;
const MAX_PAGES = 200;

/**
 * Walk GET company and keep companies whose sage_customer_code is null or blank.
 * Does not call any other route. Shared codes such as VET004 are not treated as duplicates.
 */
export async function collectCompaniesMissingSageCustomerCode(
  deps: MissingSageCustomerScanDeps
): Promise<MissingSageCustomerScan> {
  const rows: MissingSageCustomerRow[] = [];
  let companyTotal = 0;
  let companiesDone = 0;
  let incomplete = false;

  for (let page = 1; page <= MAX_PAGES; page++) {
    if (deps.isCancelled?.()) break;

    const batch = await deps.listCompanies(page, PAGE_LIMIT);
    if (!batch) {
      incomplete = true;
      break;
    }

    if (batch.total > companyTotal) companyTotal = batch.total;
    for (const company of batch.results) {
      if (company.id == null) continue;
      if (!isBlankSageCustomerCode(readSageCustomerCode(company))) continue;
      rows.push({
        companyId: company.id,
        name: company.name?.trim() ? company.name : '—',
        company,
      });
    }

    companiesDone += batch.results.length;
    deps.onProgress?.({ companiesDone, companyTotal });

    if (batch.results.length === 0 || batch.results.length < PAGE_LIMIT) break;
    if (companyTotal > 0 && companiesDone >= companyTotal) break;
    if (page === MAX_PAGES) incomplete = true;
  }

  rows.sort((a, b) => a.companyId - b.companyId);

  return { rows, companiesDone, companyTotal, incomplete };
}
